import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MFASettings } from '../components/MFASettings';
import { apiPost } from '../services/api';
import '../styles/FormPages.css';

export function UserProfilePage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'security'>('profile');
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // MFA status (will be fetched from user profile)
  const [mfaEnabled, setMfaEnabled] = useState(false);

  useEffect(() => {
    // TODO: Fetch user's MFA status from backend
    // For now, assume it's in the user object
    setMfaEnabled(false); // Placeholder
  }, []);

  const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    setPasswordError('');
  };

  const validatePasswordChange = (): boolean => {
    if (!passwordData.currentPassword) {
      setPasswordError('Current password is required');
      return false;
    }

    if (!passwordData.newPassword) {
      setPasswordError('New password is required');
      return false;
    }

    if (passwordData.newPassword.length < 10) {
      setPasswordError('New password must be at least 10 characters long');
      return false;
    }

    // Check password complexity
    const hasUpper = /[A-Z]/.test(passwordData.newPassword);
    const hasLower = /[a-z]/.test(passwordData.newPassword);
    const hasNumber = /[0-9]/.test(passwordData.newPassword);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword);

    if (!hasUpper || !hasLower || !hasNumber || !hasSpecial) {
      setPasswordError('Password must contain uppercase, lowercase, number, and special character');
      return false;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return false;
    }

    return true;
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswordChange()) {
      return;
    }

    setChangingPassword(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      const response = await apiPost('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to change password');
      }

      setPasswordSuccess('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordChange(false);

      // Auto-hide success message after 5 seconds
      setTimeout(() => setPasswordSuccess(''), 5000);
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleMFASetupComplete = () => {
    setShowMFASetup(false);
    setMfaEnabled(true);
    // TODO: Refresh user profile to get updated MFA status
  };

  if (!user) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="form-page">
      <div className="page-header">
        <h2>My Profile</h2>
        <p>Manage your account settings and security</p>
      </div>

      {/* Tab Navigation */}
      <div className="tabs" style={{ marginBottom: '2rem' }}>
        <button
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderBottom: activeTab === 'profile' ? '3px solid #5B2C86' : '3px solid transparent',
            background: 'none',
            cursor: 'pointer',
            fontWeight: activeTab === 'profile' ? 600 : 400,
            color: activeTab === 'profile' ? '#5B2C86' : '#666'
          }}
        >
          Profile Information
        </button>
        <button
          className={`tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderBottom: activeTab === 'security' ? '3px solid #5B2C86' : '3px solid transparent',
            background: 'none',
            cursor: 'pointer',
            fontWeight: activeTab === 'security' ? 600 : 400,
            color: activeTab === 'security' ? '#5B2C86' : '#666'
          }}
        >
          Security Settings
        </button>
      </div>

      {/* Profile Information Tab */}
      {activeTab === 'profile' && (
        <div className="card">
          <h3>Profile Information</h3>

          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={user.firstName}
                  disabled
                  style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>

              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={user.lastName}
                  disabled
                  style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={user.email}
                  disabled
                  style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>

              <div className="form-group">
                <label>Role</label>
                <input
                  type="text"
                  value={user.role}
                  disabled
                  style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>
            </div>

            <div className="alert alert-info" style={{ marginTop: '1rem', background: '#E3F2FD', border: '1px solid #2196F3', padding: '1rem', borderRadius: '4px' }}>
              <strong>ℹ️ Profile Updates</strong>
              <p style={{ margin: '0.5rem 0 0 0', color: '#0D47A1' }}>
                To update your profile information, please contact your administrator.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Security Settings Tab */}
      {activeTab === 'security' && (
        <div>
          {passwordSuccess && (
            <div className="alert alert-success" style={{ marginBottom: '1rem', background: '#d4edda', border: '1px solid #c3e6cb', color: '#155724', padding: '1rem', borderRadius: '4px' }}>
              {passwordSuccess}
            </div>
          )}

          {/* Password Change Section */}
          <div className="card" style={{ marginBottom: '2rem' }}>
            <h3>Password</h3>

            {!showPasswordChange ? (
              <div>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  Change your password to keep your account secure.
                </p>
                <button
                  className="button-primary"
                  onClick={() => setShowPasswordChange(true)}
                >
                  Change Password
                </button>
              </div>
            ) : (
              <form onSubmit={handlePasswordChange}>
                {passwordError && (
                  <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                    {passwordError}
                  </div>
                )}

                <div className="form-section">
                  <div className="form-group">
                    <label htmlFor="currentPassword">Current Password *</label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordInputChange}
                      required
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="newPassword">New Password *</label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordInputChange}
                      required
                    />
                    <small style={{ color: '#666', fontSize: '0.85rem' }}>
                      Must be at least 10 characters with uppercase, lowercase, number, and special character
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm New Password *</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordInputChange}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                  <button
                    type="submit"
                    className="button-primary"
                    disabled={changingPassword}
                  >
                    {changingPassword ? 'Changing Password...' : 'Change Password'}
                  </button>
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={() => {
                      setShowPasswordChange(false);
                      setPasswordData({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                      });
                      setPasswordError('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* MFA Section */}
          <div className="card">
            <h3>Two-Factor Authentication (MFA)</h3>

            {!mfaEnabled && !showMFASetup && (
              <div>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  Add an extra layer of security to your account by enabling two-factor authentication.
                  You&apos;ll need to enter a code from your authenticator app each time you log in.
                </p>
                <button
                  className="button-primary"
                  onClick={() => setShowMFASetup(true)}
                >
                  Enable Two-Factor Authentication
                </button>
              </div>
            )}

            {mfaEnabled && !showMFASetup && (
              <div>
                <div className="alert alert-success" style={{ marginBottom: '1rem', background: '#d4edda', border: '1px solid #c3e6cb', color: '#155724', padding: '1rem', borderRadius: '4px' }}>
                  ✓ Two-factor authentication is enabled on your account
                </div>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  Your account is protected with two-factor authentication.
                </p>
                {/* TODO: Add disable MFA functionality */}
                <button
                  className="button-secondary"
                  onClick={() => {
                    if (confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
                      // TODO: Implement MFA disable
                      alert('MFA disable functionality coming soon');
                    }
                  }}
                >
                  Disable Two-Factor Authentication
                </button>
              </div>
            )}

            {showMFASetup && (
              <div style={{ marginTop: '1.5rem' }}>
                <MFASettings onSetupComplete={handleMFASetupComplete} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
